import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface ConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: React.ReactNode;
    confirmText?: string;
    cancelText?: string;
    confirmButtonColor?: string; // 'blue' | 'red' | 'green' | 'purple'
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
    confirmButtonColor = 'blue'
}) => {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    if (!isOpen || !mounted) return null;

    const getColorClasses = (color: string) => {
        switch (color) {
            case 'red': return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
            case 'green': return 'bg-green-600 hover:bg-green-700 focus:ring-green-500';
            case 'purple': return 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500';
            default: return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'; // blue
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[2000] flex items-center justify-center p-4 animate-fadeIn" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden transform transition-all scale-100 ring-1 ring-black/5" onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                    <div className="flex items-start space-x-4">
                        {/* Icon based on intent? For now generic warning triangle if title contains warning icons or just generic */}
                        <div className="flex-shrink-0">
                            <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full ${confirmButtonColor === 'red' ? 'bg-red-100' : 'bg-blue-100'}`}>
                                <svg className={`h-6 w-6 ${confirmButtonColor === 'red' ? 'text-red-600' : 'text-blue-600'}`} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                            </div>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 mb-2 leading-6">{title}</h3>
                            <div className="text-gray-500 text-sm mb-4 leading-relaxed">
                                {message}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-2 bg-gray-50 -mx-6 -mb-6 px-6 py-3 border-t border-gray-100">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition shadow-sm font-medium text-sm"
                        >
                            {cancelText}
                        </button>
                        <button
                            onClick={() => {
                                onConfirm();
                                onClose();
                            }}
                            className={`px-4 py-2 text-white rounded-lg transition font-bold shadow-md text-sm ${getColorClasses(confirmButtonColor)}`}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};
